import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: #F8F8F8;
  padding: 60px 20px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AgreementCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Header = styled.div`
  padding: 32px;
  border-bottom: 2px solid #e5e7eb;
  text-align: center;
  background: #f9fafb;
  border-radius: 16px 16px 0 0;

  h1 {
    color: #111827;
    font-size: 28px;
    font-weight: bold;
    margin: 0 0 8px 0;
  }

  p {
    color: #6b7280;
    font-size: 15px;
    margin: 0;
  }
`;

const Content = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 28px;

  h2 {
    color: #374151;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;
  }

  h3 {
    color: #4b5563;
    font-size: 16px;
    font-weight: 600;
    margin: 20px 0 8px 0;
  }

  p {
    color: #6b7280;
    font-size: 14px;
    line-height: 1.6;
    margin: 0 0 12px 0;
  }

  ul {
    color: #6b7280;
    font-size: 14px;
    line-height: 1.8;
    margin: 0 0 16px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 6px;
  }
`;

const ImportantNote = styled.div`
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;

  h4 {
    color: #92400e;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '⚠️';
      font-size: 16px;
    }
  }

  p {
    color: #92400e;
    font-size: 13px;
    margin: 0;
    line-height: 1.5;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  input {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    cursor: pointer;
  }

  label {
    color: #374151;
    font-size: 14px;
    line-height: 1.5;
    cursor: pointer;
    flex: 1;

    strong {
      font-weight: 600;
    }
  }
`;

const Footer = styled.div`
  padding: 24px 32px;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &.primary {
    background: #F1AF59;
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(241, 175, 89, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const ProgressBar = styled.div`
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  margin-bottom: 8px;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  background: #F1AF59;
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ScrollIndicator = styled.div`
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
  margin-top: 8px;
`;

const ParentAgreement = () => {
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    photo: false,
    communication: false
  });

  const allAgreed = Object.values(agreements).every(v => v);

  const handleScroll = (e) => {
    const element = e.target;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    setScrollProgress(Math.min(scrollPercentage, 100));
  };

  const handleAgree = () => {
    // Store agreement acceptance
    localStorage.setItem('parentAgreementAccepted', JSON.stringify({
      acceptedAt: new Date().toISOString(),
      agreements: agreements
    }));

    // Navigate to portal home
    navigate('/portal');
  };

  const handleDecline = () => {
    if (window.confirm('If you decline the agreement, you will not be able to access the parent portal. Are you sure?')) {
      navigate('/portal/login');
    }
  };

  return (
    <Container>
      <AgreementCard>
        <Header>
          <h1>Parent Portal Agreement</h1>
          <p>Please read and accept the following terms to access the parent portal</p>
        </Header>

        <Content onScroll={handleScroll}>
          <Section>
            <h2>1. Terms of Service</h2>
            <p>
              Welcome to the Uplus Education Parent Portal. By accessing and using this portal,
              you agree to be bound by these terms and conditions. This portal is designed to
              facilitate communication between parents and Uplus Education regarding their
              children's educational progress.
            </p>
            <h3>1.1 Portal Access</h3>
            <ul>
              <li>Access to the portal is granted exclusively to registered parents or legal guardians</li>
              <li>You must maintain the confidentiality of your login credentials</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>Unauthorized access or sharing of login credentials is strictly prohibited</li>
            </ul>
            <h3>1.2 Acceptable Use</h3>
            <ul>
              <li>The portal must be used solely for educational purposes related to your child</li>
              <li>You agree not to misuse, abuse, or attempt to breach portal security</li>
              <li>Content accessed through the portal is for personal use only</li>
              <li>Commercial use or redistribution of portal content is prohibited</li>
            </ul>
          </Section>

          <Section>
            <h2>2. Privacy Policy</h2>
            <p>
              Uplus Education is committed to protecting the privacy of our students and their families.
              This privacy policy outlines how we collect, use, and protect your information.
            </p>
            <h3>2.1 Information Collection</h3>
            <ul>
              <li>Personal information: name, contact details, and relationship to student</li>
              <li>Educational records: grades, attendance, and progress reports</li>
              <li>Communication history: messages, feedback, and correspondence</li>
              <li>Usage data: login times, pages visited, and features accessed</li>
            </ul>
            <h3>2.2 Information Use</h3>
            <ul>
              <li>To provide educational services and support to your child</li>
              <li>To communicate important updates and announcements</li>
              <li>To improve our educational programs and services</li>
              <li>To ensure portal security and prevent unauthorized access</li>
            </ul>
            <h3>2.3 Information Protection</h3>
            <p>
              We implement industry-standard security measures to protect your information,
              including encryption, secure servers, and regular security audits. Your information
              will never be sold or shared with third parties without your explicit consent,
              except as required by law.
            </p>
          </Section>

          <Section>
            <h2>3. Photo and Media Consent</h2>
            <p>
              During educational activities, Uplus Education may capture photos or videos
              of students for educational and promotional purposes.
            </p>
            <h3>3.1 Usage of Photos/Videos</h3>
            <ul>
              <li>Internal educational materials and presentations</li>
              <li>School newsletters and internal communications</li>
              <li>Social media posts (with additional consent)</li>
              <li>Marketing materials (with additional consent)</li>
            </ul>
            <h3>3.2 Opt-Out Rights</h3>
            <p>
              You have the right to opt-out of photo/video consent at any time by contacting
              the administration. Students who opt-out will not be included in any media capture.
            </p>
          </Section>

          <Section>
            <h2>4. Communication Guidelines</h2>
            <p>
              To ensure effective communication between parents and educators:
            </p>
            <ul>
              <li>All communication should be respectful and professional</li>
              <li>Response times: Teachers will respond within 48 hours during school days</li>
              <li>Emergency contacts should be made through the main office</li>
              <li>Scheduled meetings are preferred for detailed discussions</li>
              <li>Communication history will be retained for record-keeping purposes</li>
            </ul>
          </Section>

          <ImportantNote>
            <h4>Important Notice</h4>
            <p>
              By accepting this agreement, you acknowledge that you have read, understood,
              and agree to all terms and conditions outlined above. This agreement is effective
              immediately upon acceptance and remains valid throughout your child's enrollment
              at Uplus Education.
            </p>
          </ImportantNote>

          <Section>
            <h2>5. Agreement Checkboxes</h2>
            <CheckboxContainer>
              <input
                type="checkbox"
                id="terms"
                checked={agreements.terms}
                onChange={(e) => setAgreements({ ...agreements, terms: e.target.checked })}
              />
              <label htmlFor="terms">
                <strong>Terms of Service:</strong> I have read and agree to the terms of service
                for using the Uplus Education Parent Portal.
              </label>
            </CheckboxContainer>

            <CheckboxContainer>
              <input
                type="checkbox"
                id="privacy"
                checked={agreements.privacy}
                onChange={(e) => setAgreements({ ...agreements, privacy: e.target.checked })}
              />
              <label htmlFor="privacy">
                <strong>Privacy Policy:</strong> I understand and accept the privacy policy
                regarding the collection and use of personal information.
              </label>
            </CheckboxContainer>

            <CheckboxContainer>
              <input
                type="checkbox"
                id="photo"
                checked={agreements.photo}
                onChange={(e) => setAgreements({ ...agreements, photo: e.target.checked })}
              />
              <label htmlFor="photo">
                <strong>Photo/Media Consent:</strong> I consent to my child being photographed
                or recorded for educational purposes (can be opted out later).
              </label>
            </CheckboxContainer>

            <CheckboxContainer>
              <input
                type="checkbox"
                id="communication"
                checked={agreements.communication}
                onChange={(e) => setAgreements({ ...agreements, communication: e.target.checked })}
              />
              <label htmlFor="communication">
                <strong>Communication Guidelines:</strong> I agree to follow the communication
                guidelines when interacting with teachers and staff.
              </label>
            </CheckboxContainer>
          </Section>
        </Content>

        <Footer>
          <ProgressBar>
            <Progress $progress={scrollProgress} />
          </ProgressBar>
          <ScrollIndicator>
            {scrollProgress < 100
              ? `Please scroll to read the entire agreement (${Math.round(scrollProgress)}% complete)`
              : '✓ You have read the entire agreement'}
          </ScrollIndicator>
          <ButtonGroup>
            <Button className="secondary" onClick={handleDecline}>
              Decline
            </Button>
            <Button
              className="primary"
              onClick={handleAgree}
              disabled={!allAgreed || scrollProgress < 95}
              title={!allAgreed ? 'Please check all agreement boxes' : scrollProgress < 95 ? 'Please read the entire agreement' : ''}
            >
              I Agree and Continue
            </Button>
          </ButtonGroup>
        </Footer>
      </AgreementCard>
    </Container>
  );
};

export default ParentAgreement;